import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import {
  authGuard,
  authChildGuard,
  adminGuard,
  clientAreaGuard,
  intermediaryGuard,
  commercialGuard,
  crmAdmGuard,
  crmMediadorGuard,
} from './shared/guard';
import { SelectivePreloadStrategy } from './shared/services/selective-preload-strategy.service';

const routes: Routes = [
  // 1) URL raiz '' agora é a canonical home. Não redirecionar para /home.

  // 3) Rotas protegidas
  {
    path: '',
    component: PagesComponent,
    canActivate: [authGuard], // Protege todas as rotas filhas
    canActivateChild: [authChildGuard], // Protege rotas aninhadas
    data: { All: 1 }, // ✅ Permite acesso ao shell principal para autenticados
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
        data: { breadcrumb: 'Inicio', All: 1, preload: true },
        pathMatch: 'full',
      },
      { path: 'home', redirectTo: '', pathMatch: 'full' },
      {
        path: 'rgpdnotify',
        loadChildren: () => import('./pages/rgpdnotify/rgpdnotify.module').then(m => m.RgpdnotifyModule),
        data: { breadcrumb: 'RgpdnotifyComponent', All: 1 },
      },
      {
        path: 'dl/:token',
        loadComponent: () =>
          import('./shared/components/deep-link-handler/deep-link-handler.component').then(
            m => m.DeepLinkHandlerComponent,
          ),
        data: { breadcrumb: 'DeepLink', All: 1 },
      },
      {
        path: 'commercialarea',
        loadChildren: () => import('./pages/commercialarea/commercialarea.module').then(m => m.CommercialareaModule),
        data: { breadcrumb: 'Área Comercial', CommercialArea: 1 },
        canActivate: [commercialGuard],
      },
      {
        path: 'intermediaryarea',
        loadChildren: () =>
          import('./pages/intermediaryarea/intermediaryarea.module').then(m => m.IntermediaryareaModule),
        data: { breadcrumb: 'Área Mediador', IntermediaryArea: 1, preload: true },
        canActivate: [intermediaryGuard],
      },
      {
        path: 'clientarea',
        loadChildren: () => import('./pages/clientarea/clientarea.module').then(m => m.ClientareaModule),
        data: { breadcrumb: 'Área do Cliente', ClientArea: 1, preload: true },
        canActivate: [clientAreaGuard],
      },
      {
        path: 'consultar-sinistro/:id',
        loadComponent: () =>
          import('./pages/clientarea/insuranceclaims/consultar-sinistro/consultar-sinistro.component').then(
            m => m.ConsultarSinistroComponent,
          ),
        data: { breadcrumb: 'Consultar Sinistro', ClientArea: 1 },
        canActivate: [clientAreaGuard],
      },
      {
        path: 'participar-sinistro',
        loadComponent: () =>
          import('./pages/clientarea/insuranceclaims/insurance-claim-notification/insurance-claim-notification.component').then(
            m => m.InsuranceClaimNotificationComponent,
          ),
        data: { breadcrumb: 'Participar Sinistro', All: 1 },
      },
      {
        path: 'sitemanagement',
        loadChildren: () => import('./pages/sitemanagement/sitemanagement.module').then(m => m.SitemanagementModule),
        data: { breadcrumb: 'Área de Gestão', All: 0, Admin: 1, SiteManagement: 1, preload: true },
        canActivate: [adminGuard],
      },
      {
        path: 'vault',
        loadComponent: () =>
          import('./shared/components/accessmanager/accessmanager.component').then(m => m.AccessmanagerComponent),
        data: { breadcrumb: 'Gestor de Passwords', All: 0, Admin: 1, SiteManagement: 1 },
        canActivate: [authGuard],
      },
      {
        path: 'permissions',
        loadComponent: () =>
          import('./shared/components/userpermissions/userpermissions.component').then(m => m.UserpermissionsComponent),
        data: { breadcrumb: 'Gestão de Permissões', All: 0, Admin: 1, SiteManagement: 1 },
        canActivate: [authGuard],
      },
      {
        path: 'telemedicina',
        loadComponent: () =>
          import('./pages/telemedicina/telemedicina-platform.component').then(m => m.TelemedicinaPlatformComponent),
        data: { breadcrumb: 'Rede Médica & Telemedicina', All: 1 },
        canActivate: [adminGuard],
      },
      {
        path: 'telemedicina/:section',
        loadComponent: () =>
          import('./pages/telemedicina/telemedicina-platform.component').then(m => m.TelemedicinaPlatformComponent),
        data: { breadcrumb: 'Rede Médica & Telemedicina', All: 1 },
        canActivate: [adminGuard],
      },
      {
        path: 'seguros/:slug',
        loadComponent: () =>
          import('./pages/seguros/seguro-detail/seguro-detail.component').then(m => m.SeguroDetailComponent),
        data: { breadcrumb: 'Detalhe do Seguro', All: 1 },
      },
      {
        path: 'seguros/:slug/:subslug',
        loadComponent: () =>
          import('./pages/seguros/seguro-detail/seguro-detail.component').then(m => m.SeguroDetailComponent),
        data: { breadcrumb: 'Simulação', All: 1 },
      },
      {
        path: 'crm',
        loadChildren: () => import('./pages/crm/crm.module').then(m => m.CrmModule),
        data: {
          breadcrumb: 'CRM ADM',
          All: 1,
        },
        canActivate: [crmAdmGuard],
      },
      {
        path: 'crm-mediador',
        loadChildren: () => import('./pages/crm-mediador/crm-mediador.module').then(m => m.CrmMediadorModule),
        data: {
          breadcrumb: 'CRM Mediadores',
          All: 1,
        },
        canActivate: [crmMediadorGuard],
      },
      {
        path: 'admin',
        loadChildren: () => import('./pages/admin/admin.module').then(m => m.AdminModule),
        data: { breadcrumb: 'Administração', All: 0, Admin: 1, SiteManagement: 1 },
        canActivate: [adminGuard],
      },
      // Nota: A área /marketing/ foi descontinuada — todas as funcionalidades
      // estão agora integradas no CRM (/crm/email-marketing, /crm/automations)
      {
        path: 'project-manager',
        loadComponent: () =>
          import('./shared/components/project-manager/project-manager.component').then(m => m.ProjectManagerComponent),
        data: {
          breadcrumb: 'Gestor de Projetos',
          All: 0,
          Admin: 1,
          SiteManagement: 1,
          CommercialArea: 1,
          IntermediaryArea: 1,
        },
        canActivate: [intermediaryGuard],
      },
      {
        path: 'sendinvitations',
        loadComponent: () =>
          import('./shared/components/sendinvitations/sendinvitations.component').then(m => m.SendinvitationsComponent),
        data: { breadcrumb: 'Gestão de Convites', All: 0, Admin: 1, SiteManagement: 1 },
        canActivate: [adminGuard],
      },
      {
        path: 'guias',
        loadComponent: () => import('./pages/blog/list/blog-list.component').then(m => m.BlogListComponent),
        data: { breadcrumb: 'Blog & Dicas', All: 1 },
      },
      {
        path: 'guias/categoria/:categorySlug',
        loadComponent: () => import('./pages/blog/list/blog-list.component').then(m => m.BlogListComponent),
        data: { breadcrumb: 'Categoria', All: 1 },
      },
      {
        path: 'guias/:slug',
        loadComponent: () => import('./pages/blog/detail/blog-detail.component').then(m => m.BlogDetailComponent),
        data: { breadcrumb: 'Artigo', All: 1 },
      },
    ],
  },

  // Login - só para não autenticados
  {
    path: 'login',
    loadChildren: () => import('./pages/security/login/login.module').then(m => m.LoginModule),
    data: { preload: true },
  },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./pages/security/login/maintenance/maintenance.component').then(m => m.MaintenanceComponent),
  },
  {
    path: 'aprovaldocument',
    loadComponent: () =>
      import('./pages/aprovaldocument/aprovaldocument.component').then(m => m.AprovaldocumentComponent),
  },
  {
    path: 'privacidade',
    loadComponent: () => import('./pages/privacidade/privacidade.component').then(m => m.PrivacidadeComponent),
  },
  { path: 'rgpd', redirectTo: 'privacidade', pathMatch: 'full' },
  {
    path: 'termos',
    loadComponent: () => import('./pages/termos/termos.component').then(m => m.TermosComponent),
  },
  {
    path: 'sobre',
    loadComponent: () => import('./pages/home/aboutus/aboutus.component').then(m => m.AboutusComponent),
  },
  {
    path: 'contactos',
    loadComponent: () => import('./pages/home/contacts/contacts.component').then(m => m.ContactsComponent),
  },
  {
    path: 'blog',
    redirectTo: 'guias',
    pathMatch: 'full',
  },
  {
    path: 'blog/:slug',
    redirectTo: 'guias/:slug',
    pathMatch: 'full',
  },

  {
    path: 'data-deletion',
    loadComponent: () => import('./pages/data-deletion/data-deletion.component').then(m => m.DataDeletionComponent),
  },
  {
    path: 'unsubscribe',
    redirectTo: 'unsubscribe-success',
    pathMatch: 'full',
  },
  {
    path: 'unsubscribe-success',
    loadComponent: () =>
      import('./pages/unsubscribe-success/unsubscribe-success.component').then(m => m.UnsubscribeSuccessComponent),
  },

  // Autenticação Magic Link via email (não carece de login, tem token na query str)
  {
    path: 'auth/magic',
    loadComponent: () =>
      import('./pages/security/login/magic-link-handler/magic-link-handler.component').then(
        m => m.MagicLinkHandlerComponent,
      ),
    data: { preload: true },
  },

  // Rota pública para links de divulgação / embaixadores (ex: segurosvirtual.pt/r/ref-12345)
  {
    path: 'r/:code',
    loadComponent: () =>
      import('./pages/referral-redirect/referral-redirect.component').then(m => m.ReferralRedirectComponent),
  },

  // Rota pública para links de convite de clientes Convida & Ganha (ex: segurosvirtual.pt/convite/rita-fernandes-8q3f)
  {
    path: 'convite/:code',
    loadComponent: () =>
      import('./pages/referral-redirect/referral-redirect.component').then(m => m.ReferralRedirectComponent),
  },

  // 📝 Rotas públicas para assinatura mobile de sinistros no telemóvel (Feedback #396)
  {
    path: 'claims/signature',
    loadComponent: () =>
      import('./pages/claims-mobile-signature/claims-mobile-signature.component').then(
        m => m.ClaimsMobileSignatureComponent,
      ),
  },
  {
    path: 'claims/signature/:token',
    loadComponent: () =>
      import('./pages/claims-mobile-signature/claims-mobile-signature.component').then(
        m => m.ClaimsMobileSignatureComponent,
      ),
  },
  {
    path: 'claims/signature-b-demo',
    loadComponent: () =>
      import('./pages/claims-mobile-signature/claims-mobile-signature.component').then(
        m => m.ClaimsMobileSignatureComponent,
      ),
  },
  {
    path: 'assinar-sinistro',
    loadComponent: () =>
      import('./pages/claims-mobile-signature/claims-mobile-signature.component').then(
        m => m.ClaimsMobileSignatureComponent,
      ),
  },
  {
    path: 'assinar-sinistro/:token',
    loadComponent: () =>
      import('./pages/claims-mobile-signature/claims-mobile-signature.component').then(
        m => m.ClaimsMobileSignatureComponent,
      ),
  },

  // 🏥 Sala Segura e Isolada de Teleconsulta por Vídeo/Áudio (/teleconsulta/:token)
  {
    path: 'teleconsulta/:token',
    loadComponent: () =>
      import('./pages/telemedicina/teleconsulta-room/teleconsulta-room.component').then(
        m => m.TeleconsultaRoomComponent,
      ),
  },

  // 4) Fallback — redireciona para a homepage raiz
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: SelectivePreloadStrategy,
      useHash: false,
      initialNavigation: 'enabledNonBlocking',
      scrollPositionRestoration: 'top',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
